import React from "react";
import { cn } from "@shared/lib/utils";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";

export interface AdjustmentCardProps {
  title: string;
  description: string;
  isActive: boolean;
  onToggle: (checked: boolean) => void;
  icon: React.ReactNode;
  stats: {
    label1: string;
    value1: string | number;
    subLabel1?: string;
    label2: string;
    value2: string;
    subLabel2?: string;
  };
  variant?: "blue" | "indigo" | "purple";
  disabled?: boolean;
}

export const AdjustmentCard = ({
  title,
  description,
  isActive,
  onToggle,
  icon,
  stats,
  variant = "blue",
  disabled = false,
}: AdjustmentCardProps) => {
  const variantClasses = {
    blue: disabled
      ? "bg-neutral-50/50 border-neutral-100"
      : "bg-blue-50/50 border-blue-100",
    indigo: disabled
      ? "bg-neutral-50/50 border-neutral-100"
      : "bg-indigo-50/50 border-indigo-100",
    purple: disabled
      ? "bg-neutral-50/50 border-neutral-100"
      : "bg-purple-50/50 border-purple-100",
  };

  const iconClasses = {
    blue: disabled
      ? "bg-neutral-100 text-neutral-400"
      : "bg-blue-100 text-blue-600",
    indigo: disabled
      ? "bg-neutral-100 text-neutral-400"
      : "bg-indigo-100 text-indigo-600",
    purple: disabled
      ? "bg-neutral-100 text-neutral-400"
      : "bg-purple-100 text-purple-600",
  };

  return (
    <Card
      className={cn(
        "transition-all duration-300 border shadow-sm flex flex-col",
        variantClasses[variant],
        isActive && !disabled ? "ring-1 ring-blue-200" : "opacity-90",
      )}
    >
      <CardContent className="p-2 flex flex-col flex-1">
        {/* Header */}
        <div className="flex flex-col gap-2 mb-3">
          <div className="flex items-center justify-between">
            <div
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                iconClasses[variant],
              )}
            >
              {icon}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className={cn(
                  "text-[10px] font-bold tracking-wider",
                  isActive && !disabled ? "text-blue-600" : "text-neutral-400",
                )}
              >
                {isActive ? "ACTIVE" : "INACTIVE"}
              </span>
              <Switch
                checked={isActive}
                onCheckedChange={onToggle}
                disabled={disabled}
                className="disabled:opacity-50"
              />
            </div>
          </div>
          <div>
            <h3
              className={cn(
                "font-bold text-base leading-tight mb-1",
                disabled ? "text-neutral-500" : "text-neutral-800",
              )}
            >
              {title}
            </h3>
            <p className="text-xs text-neutral-500 leading-relaxed">
              {description}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 mt-auto">
          <div className="bg-white p-2 rounded-lg border border-neutral-100 shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
            <p className="text-[10px] font-bold text-neutral-400  tracking-tight mb-1">
              {stats.label1}
            </p>
            <p
              className={cn(
                "text-xl font-bold",
                disabled ? "text-neutral-500" : "text-neutral-800",
              )}
            >
              {stats.value1}
            </p>
            {stats.subLabel1 && (
              <p className="text-[10px] text-neutral-400 font-medium mt-0.5">
                {stats.subLabel1}
              </p>
            )}
          </div>
          <div className="bg-white p-2 rounded-lg border border-neutral-100 shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
            <p className="text-[10px] font-bold text-neutral-400  tracking-tight mb-1">
              {stats.label2}
            </p>
            <p
              className={cn(
                "text-xl font-bold",
                disabled ? "text-neutral-400" : "text-green-600",
              )}
            >
              {stats.value2}
            </p>
            {stats.subLabel2 && (
              <p className="text-[10px] text-neutral-400 font-medium mt-0.5">
                {stats.subLabel2}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
